/* VOLT box flow — <volt-box-flow>
   A guided, mobile-first box builder: one viewport, four beats
   (Size → Flavors → Delivery → Review), no page scrolling.

   The box being built is a persistent summary near the top of the screen:
   a fill ring, every can added so far (tap to open the editor sheet), and
   a clear control. The contextual CTA stays at the bottom of the flow.
   Flavors are browsed by brand tab + a horizontally swiped tile grid, so
   the catalog never pushes the box out of view.

   Data in:  the same two JSON <script> tags the classic builder uses
             (snippets/volt-builder-flow.liquid renders them on Shopify;
             preview pages inject them from mock-catalog.js).
   Data out: POST /cart/add.js with one size-tier drink variant per flavor.
             Shopify totals the actual cans selected instead of a flat box SKU.
   State:    localStorage key ("volt-box"), shared with the classic full-page
             builder so either page can restore an in-progress mix. */
(function () {
  'use strict';

  if (customElements.get('volt-box-flow')) return;

  var STORE_KEY = 'volt-box';
  var SYNC_EVENT = 'volt:box-change';
  var STEP_KEY = 'volt-flow-step';
  var STEPS = ['size', 'flavors', 'plan', 'review'];
  var STEP_LABELS = { size: 'Size', flavors: 'Flavors', plan: 'Delivery', review: 'Review' };

  var memoryState = null;

  function readJson(selector) {
    var node = document.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  /* DOM helper: h('button', {class: 'x', onclick: fn}, child, ...).
     Text children go through createTextNode, so catalog strings are never
     parsed as HTML. */
  function h(tag, attrs) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (key) {
        var val = attrs[key];
        if (val === null || val === undefined || val === false) return;
        if (key.indexOf('on') === 0 && typeof val === 'function') {
          node.addEventListener(key.slice(2), val);
        } else if (val === true) {
          node.setAttribute(key, '');
        } else {
          node.setAttribute(key, String(val));
        }
      });
    }
    for (var i = 2; i < arguments.length; i++) {
      var child = arguments[i];
      if (child === null || child === undefined || child === false) continue;
      node.appendChild(typeof child === 'string' ? document.createTextNode(child) : child);
    }
    return node;
  }

  function loadState() {
    try {
      var raw = window.localStorage.getItem(STORE_KEY);
      if (raw) return JSON.parse(raw);
    } catch (e) { /* fall through to memory */ }
    return memoryState;
  }

  function saveState(state) {
    memoryState = state;
    try {
      window.localStorage.setItem(STORE_KEY, JSON.stringify(state));
    } catch (e) { /* memoryState still carries it for this page */ }
  }

  function loadStep() {
    try {
      return window.sessionStorage.getItem(STEP_KEY);
    } catch (e) {
      return null;
    }
  }

  function saveStep(step) {
    try {
      window.sessionStorage.setItem(STEP_KEY, step);
    } catch (e) { /* fine — refresh restarts at a computed step */ }
  }

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* "$28.00" → 28. Returns null when the money string can't be parsed
     (unusual currency formats) — callers then skip per-can math. */
  function moneyNumber(str) {
    var n = parseFloat(String(str || '').replace(/[^0-9.]/g, ''));
    return isNaN(n) ? null : n;
  }

  function moneyFromCents(cents) {
    return '$' + (Math.round(Number(cents) || 0) / 100).toFixed(2);
  }

  class VoltBoxFlow extends HTMLElement {
    connectedCallback() {
      var self = this;

      this.unbindListeners();
      this._onSync = function (evt) {
        if (evt.detail && evt.detail.source === self) return;
        if (!self._ready) return;
        self.restoreOrInitState();
        self.renderStage();
        self.update();
      };
      this._onStorage = function (evt) {
        if (evt.key !== STORE_KEY || !self._ready) return;
        self.restoreOrInitState();
        self.renderStage();
        self.update();
      };
      this._onPageShow = function (evt) {
        if (!evt.persisted || !self._ready) return;
        self._submitting = false;
        self.restoreOrInitState();
        self.renderStage();
        self.update();
      };
      document.addEventListener(SYNC_EVENT, this._onSync);
      window.addEventListener('storage', this._onStorage);
      window.addEventListener('pageshow', this._onPageShow);

      if (this._initialized) {
        if (this._ready) {
          this.restoreOrInitState();
          this.renderStage();
          this.update();
        }
        return;
      }
      this._initialized = true;
      this._submitting = false;
      this._ready = false;
      this._sheetOpen = false;
      this._handoff = null;

      var sectionId = this.getAttribute('section-id') || '';
      this.addUrl = this.getAttribute('add-url') || '/cart/add.js';
      this.cartUrl = this.getAttribute('cart-url') || '/cart';
      this.checkoutUrl = this.getAttribute('checkout-url') || '/checkout';
      this.afterAdd = this.getAttribute('after-add') === 'checkout' ? 'checkout' : 'cart';
      this.mock = this.hasAttribute('mock');
      this.exitUrl = this.getAttribute('exit-url') || '';

      var rawDrinks = readJson('script[data-volt-drinks="' + sectionId + '"]') || [];
      var rawBox = readJson('script[data-volt-box="' + sectionId + '"]') || {};

      var seenDrinkIds = {};
      this.allDrinks = rawDrinks.filter(function (d) {
        if (!d || !d.id || seenDrinkIds[d.id]) return false;
        seenDrinkIds[d.id] = true;
        return true;
      });
      this.drinks = this.allDrinks.filter(function (d) { return d.available !== false; });
      this.variants = (rawBox.variants || []).filter(function (v) {
        return v && v.available !== false && v.id && v.cans > 0;
      }).sort(function (a, b) {
        return a.cans - b.cans;
      });
      this.hydrateSizePricing();

      this.brands = [];
      this.allDrinks.forEach(function (d) {
        var key = d.brand || 'More';
        if (self.brands.indexOf(key) === -1) self.brands.push(key);
      });
      this.brand = this.brands[0] || '';

      if (!this.drinks.length || !this.variants.length) {
        this.renderUnavailable();
        return;
      }

      this._ready = true;
      this.restoreOrInitState();

      /* Returning visitors (a saved mix, or ?size= deep link) skip the
         parts they've already answered. */
      var urlSize = parseInt(new URLSearchParams(window.location.search).get('size') || '', 10);
      if (urlSize) {
        var urlVariant = this.variants.filter(function (v) { return v.cans === urlSize; })[0];
        if (urlVariant && urlVariant.id !== this.state.variantId) {
          this.state.variantId = urlVariant.id;
          if (this.state.mix.length > this.size) this.state.mix = this.state.mix.slice(0, this.size);
          if (this.state.planId && !this.plan) this.state.planId = null;
          this.persist();
        }
      }

      var saved = loadStep();
      var start = 'size';
      if (urlSize) start = 'flavors'; /* deep link answered the size question */
      else if (saved && STEPS.indexOf(saved) !== -1) start = saved;
      else if (this.state.mix.length) start = 'flavors';
      if (!this.stepAllowed(start)) start = this.state.mix.length ? 'flavors' : 'size';
      this.step = start;

      this.render();
      this.renderStage();
      this.update();
      this.loadCollectionCatalog(sectionId);
    }

    loadCollectionCatalog(sectionId) {
      if (this.mock || !window.fetch) return;
      var self = this;
      var dataNode = document.querySelector('script[data-volt-drinks="' + sectionId + '"]');
      var handle = dataNode && dataNode.getAttribute('data-collection-handle');
      if (!handle) return;

      window.fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=250', {
        credentials: 'same-origin'
      }).then(function (response) {
        if (!response.ok) throw new Error('Catalog request failed');
        return response.json();
      }).then(function (payload) {
        var products = payload && Array.isArray(payload.products) ? payload.products : [];
        var drinks = products.map(function (product) {
          var title = product.title || '';
          var divider = title.indexOf(' — ');
          var flavor = divider === -1 ? title : title.slice(divider + 3);
          return {
            id: product.id,
            title: title,
            brand: product.vendor || 'More',
            flavor: flavor,
            pricing: null,
            variants: (product.variants || []).map(function (variant) {
              var option = variant.option2 || variant.option1 || variant.title || '';
              var size = parseInt(option, 10);
              return { id: variant.id, size: size, priceCents: Math.round(Number(variant.price) * 100), available: variant.available !== false };
            }).filter(function (variant) { return [6, 12, 24, 48].indexOf(variant.size) !== -1; }),
            image: product.images && product.images[0] ? product.images[0].src : null,
            available: (product.variants || []).some(function (variant) { return variant.available !== false; })
          };
        }).filter(function (drink) { return drink.id; });
        if (drinks.length <= self.allDrinks.length) return;

        self.allDrinks = drinks;
        self.drinks = drinks.filter(function (drink) { return drink.available !== false; });
        self.hydrateSizePricing();
        self.brands = [];
        drinks.forEach(function (drink) {
          if (self.brands.indexOf(drink.brand) === -1) self.brands.push(drink.brand);
        });
        if (self.brands.indexOf(self.brand) === -1) self.brand = self.brands[0] || '';
        self.restoreOrInitState();
        self.renderStage();
        self.update();
      }).catch(function () { /* Embedded Liquid catalog remains the fallback. */ });
    }

    disconnectedCallback() {
      this.unbindListeners();
    }

    unbindListeners() {
      if (this._onSync) document.removeEventListener(SYNC_EVENT, this._onSync);
      if (this._onStorage) window.removeEventListener('storage', this._onStorage);
      if (this._onPageShow) window.removeEventListener('pageshow', this._onPageShow);
    }

    /* ---------- state (same shape + storage key as <volt-box-builder>) ---------- */

    restoreOrInitState() {
      var saved = loadState() || {};
      var variants = this.variants;
      var variant = null;
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].id === saved.variantId) variant = variants[i];
      }
      if (!variant) {
        var preferred = parseInt(this.getAttribute('default-size') || '', 10);
        for (var j = 0; j < variants.length; j++) {
          if (variants[j].cans === preferred) variant = variants[j];
        }
      }
      if (!variant) variant = variants[0];

      var knownIds = {};
      this.drinks.forEach(function (d) { knownIds[d.id] = true; });
      var mix = Array.isArray(saved.mix) ? saved.mix.filter(function (id) { return knownIds[id]; }) : [];
      if (mix.length > variant.cans) mix = mix.slice(0, variant.cans);

      var planId = null;
      (variant.plans || []).forEach(function (plan) {
        if (plan.id === saved.planId) planId = plan.id;
      });

      this.state = { variantId: variant.id, planId: planId, mix: mix };
      if (this.step && !this.stepAllowed(this.step)) this.step = 'flavors';
    }

    persist() {
      saveState(this.state);
      document.dispatchEvent(new CustomEvent(SYNC_EVENT, { detail: { source: this } }));
    }

    get variant() {
      var id = this.state.variantId;
      return this.variants.filter(function (v) { return v.id === id; })[0];
    }

    get plan() {
      var id = this.state.planId;
      return (this.variant.plans || []).filter(function (p) { return p.id === id; })[0] || null;
    }

    get size() {
      return this.variant.cans;
    }

    hydrateSizePricing() {
      var self = this;
      (this.variants || []).forEach(function (variant) {
        var unitPrices = (self.allDrinks || []).map(function (drink) {
          var tier = self.tierVariant(drink, variant.cans);
          if (tier && tier.available !== false && Number(tier.priceCents) > 0) return Number(tier.priceCents);
          var retail = drink.pricing && drink.pricing.retail;
          var value = retail && Number(retail[String(variant.cans)]);
          return value > 0 ? Math.round(value * 100) : null;
        }).filter(function (value) { return value !== null; });
        variant.minUnitCents = unitPrices.length ? Math.min.apply(Math, unitPrices) : 0;
        variant.minTotalCents = variant.minUnitCents * variant.cans;
        variant.price = variant.minTotalCents ? 'From ' + moneyFromCents(variant.minTotalCents) : '';
      });
    }

    tierVariant(drink, size) {
      return ((drink && drink.variants) || []).filter(function (variant) {
        return Number(variant.size) === Number(size);
      })[0] || null;
    }

    unitPriceCents(drink) {
      var tier = this.tierVariant(drink, this.size);
      if (tier && Number(tier.priceCents) > 0) return Number(tier.priceCents);
      var retail = drink && drink.pricing && drink.pricing.retail;
      var value = retail && Number(retail[String(this.size)]);
      return value > 0 ? Math.round(value * 100) : 0;
    }

    boxTotalCents() {
      var self = this;
      return this.state.mix.reduce(function (total, id) {
        return total + self.unitPriceCents(self.drinkById(id));
      }, 0);
    }

    boxTotal() {
      return moneyFromCents(this.boxTotalCents());
    }

    drinkById(id) {
      return this.allDrinks.filter(function (d) { return d.id === id; })[0];
    }

    countOf(id) {
      return this.state.mix.filter(function (x) { return x === id; }).length;
    }

    brandCount(brand) {
      var self = this;
      return this.state.mix.filter(function (id) {
        var d = self.drinkById(id);
        return d && (d.brand || 'More') === brand;
      }).length;
    }

    isFull() {
      return this.state.mix.length >= this.size;
    }

    remaining() {
      return Math.max(0, this.size - this.state.mix.length);
    }

    countLabel() {
      return this.state.mix.length + ' of ' + this.size + ' cans picked';
    }

    /* Grouped view of the mix: [{id, drink, count}] in first-added order. */
    groupedMix() {
      var self = this;
      var counts = {};
      var order = [];
      this.state.mix.forEach(function (id) {
        if (!counts[id]) { counts[id] = 0; order.push(id); }
        counts[id]++;
      });
      return order.map(function (id) {
        return { id: id, drink: self.drinkById(id), count: counts[id] };
      });
    }

    /* ---------- steps ---------- */

    stepAllowed(step) {
      if (step === 'plan' || step === 'review') return this.isFull();
      return true;
    }

    goto(step, backward) {
      if (this._submitting || step === this.step) return;
      if (!this.stepAllowed(step)) {
        this.announce('Fill your box first — ' + this.remaining() + ' more to go.');
        return;
      }
      this._handoff = null;
      this.step = step;
      saveStep(step);
      this.renderStage(backward ? 'back' : 'fwd');
      this.update();
      this.announce(STEP_LABELS[step] + ' step.');
      var stage = this.querySelector('[data-flow-stage]');
      if (stage) stage.scrollTop = 0;
    }

    back() {
      var index = STEPS.indexOf(this.step);
      if (index > 0) this.goto(STEPS[index - 1], true);
      else if (this.exitUrl) window.location.href = this.exitUrl;
    }

    /* ---------- actions ---------- */

    setVariant(id) {
      if (this._submitting || this.state.variantId === id) return;
      this.state.variantId = id;
      var trimmed = false;
      if (this.state.mix.length > this.size) {
        this.state.mix = this.state.mix.slice(0, this.size);
        trimmed = true;
      }
      if (this.state.planId && !this.plan) this.state.planId = null;
      this.persist();
      this.update();
      this.announce('Box size: ' + this.size + ' cans.' + (trimmed ? ' Extra cans were removed.' : ''));
    }

    setPlan(id) {
      if (this._submitting) return;
      this.state.planId = id;
      this.persist();
      this.update();
      var plan = this.plan;
      this.announce((plan ? plan.name : 'One-time box') + ' selected.');
    }

    addDrink(id, fromEl) {
      if (this._submitting) return;
      if (this.isFull()) {
        this.nudgeTray();
        this.announce('Your box is full. Tap the box to swap something out.');
        return;
      }
      this.state.mix.push(id);
      this.persist();
      if (fromEl) this.flyToTray(fromEl);
      this.update();
      var drink = this.drinkById(id);
      this.announce(drink.title + ' added — ' + this.countLabel() + (this.isFull() ? '. Box full!' : '.'));
    }

    removeOne(id) {
      if (this._submitting) return;
      var index = this.state.mix.lastIndexOf(id);
      if (index < 0) return;
      var removed = this.drinkById(this.state.mix[index]);
      this.state.mix.splice(index, 1);
      this.persist();
      this.update();
      this.announce((removed ? removed.title : 'Can') + ' removed — ' + this.countLabel() + '.');
    }

    removeAll(id) {
      if (this._submitting) return;
      var drink = this.drinkById(id);
      this.state.mix = this.state.mix.filter(function (x) { return x !== id; });
      this.persist();
      this.update();
      this.announce('All ' + (drink ? drink.title : 'cans') + ' removed — ' + this.countLabel() + '.');
    }

    /* Typed quantities: set a flavor to exactly n cans. Clamped to what
       still fits in the box; NaN (cleared field) keeps the current count. */
    setCount(id, n) {
      if (this._submitting) return;
      var current = this.countOf(id);
      if (isNaN(n)) { this.update(); return; }
      var max = current + this.remaining();
      var target = Math.max(0, Math.min(n, max));
      if (target === current) { this.update(); return; }
      if (target > current) {
        for (var i = 0; i < target - current; i++) this.state.mix.push(id);
      } else {
        for (var j = 0; j < current - target; j++) {
          var index = this.state.mix.lastIndexOf(id);
          if (index > -1) this.state.mix.splice(index, 1);
        }
      }
      this.persist();
      this.update();
      var drink = this.drinkById(id);
      var capped = n > max;
      if (capped) this.nudgeTray();
      this.announce((drink ? drink.title : 'Flavor') + ': ' + target + ' in the box — ' + this.countLabel() +
        (capped ? ' Only ' + target + ' fit.' : ''));
    }

    /* Small numeric field shared by tiles, review rows and the sheet.
       Commits on blur (Enter blurs); taps inside never bubble to the tile. */
    qtyInput(drink, className) {
      var self = this;
      var input = h('input', {
        type: 'text', inputmode: 'numeric', pattern: '[0-9]*', autocomplete: 'off',
        class: className,
        'aria-label': 'How many ' + drink.title,
        onclick: function (evt) { evt.stopPropagation(); },
        onfocus: function () { input.select(); },
        onkeydown: function (evt) {
          if (evt.key === 'Enter') { evt.preventDefault(); input.blur(); }
        },
        onblur: function () { self.setCount(drink.id, parseInt(input.value, 10)); }
      });
      return input;
    }

    surprise() {
      if (this._submitting || this.isFull()) return;
      var drinks = this.drinks;
      while (!this.isFull()) {
        this.state.mix.push(drinks[Math.floor(Math.random() * drinks.length)].id);
      }
      this.persist();
      this.update();
      this.pulseTray();
      this.announce('Box filled with a surprise mix — tap the box to review it.');
    }

    clear() {
      if (this._submitting) return;
      this.state.mix = [];
      this.persist();
      if (!this.stepAllowed(this.step)) this.step = 'flavors';
      this.renderStage();
      this.update();
      this.announce('Box cleared.');
    }

    /* ---------- cart handoff (same payload as <volt-box-builder>) ---------- */

    mixSummary() {
      return this.groupedMix().map(function (g) {
        return g.count + ' × ' + g.drink.title;
      }).join('; ');
    }

    buildPayload() {
      var self = this;
      var boxId = 'NB-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7);
      var items = [];
      this.groupedMix().forEach(function (group) {
        var tier = self.tierVariant(group.drink, self.size);
        if (!tier || !tier.id || tier.available === false) return;
        items.push({
          id: tier.id,
          quantity: group.count,
          properties: {
            'Mixed box': self.size + ' cans · priced per can',
            '_volt_box_id': boxId,
            '_volt_box_size': String(self.size)
          }
        });
      });
      return items.length === this.groupedMix().length ? { items: items } : null;
    }

    addToCart() {
      if (this._submitting) return;
      if (!this.isFull()) {
        this.announce('Fill ' + this.remaining() + ' more ' + (this.remaining() === 1 ? 'slot' : 'slots') + ' first.');
        return;
      }

      var payload = this.buildPayload();
      if (!payload) {
        this.announce('One of those flavors is unavailable at this box size. Remove it and try again.');
        return;
      }

      if (this.mock) {
        this._handoff = payload;
        this.renderStage();
        this.update();
        this.announce('Preview: the cart payload is shown on screen.');
        return;
      }

      var self = this;
      this._submitting = true;
      this.update();
      this.announce('Adding your box to the cart…');

      fetch(this.addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          return res.json().then(function (data) { return { ok: res.ok, data: data }; });
        })
        .then(function (result) {
          if (!result.ok) {
            var msg = (result.data && (result.data.description || result.data.message)) || 'Something went wrong. Please try again.';
            self._submitting = false;
            self.update();
            self.announce(msg);
            return;
          }
          self.state.mix = [];
          self.persist();
          window.location.href = self.afterAdd === 'checkout' ? self.checkoutUrl : self.cartUrl;
        })
        .catch(function () {
          self._submitting = false;
          self.update();
          self.announce('Could not reach the cart. Check your connection and try again.');
        });
    }

    /* ---------- shared visuals ---------- */

    announce(message) {
      var live = this.querySelector('[data-flow-status]');
      if (!live) return;
      live.textContent = '';
      window.requestAnimationFrame(function () {
        live.textContent = message;
      });
    }

    flavorLabel(drink) {
      var label = drink.flavor || drink.title || '';
      var brand = drink.brand || '';
      if (brand && label.toLowerCase().indexOf(brand.toLowerCase()) === 0) {
        var stripped = label.slice(brand.length).replace(/^[\s—–:|-]+/, '');
        if (stripped) label = stripped;
      }
      label = label.replace(/^Energy\s*[—–:|-]+\s*/i, '');
      return label;
    }

    unitPrice(drink) {
      var cents = this.unitPriceCents(drink);
      return cents > 0 ? moneyFromCents(cents) + ' / can' : '';
    }

    drinkVisual(drink) {
      if (drink.image) {
        return h('span', { class: 'volt-can volt-can--img', 'aria-hidden': 'true' },
          h('img', { src: drink.image, alt: '', loading: 'lazy', width: '72', height: '72' }));
      }
      return h('span', { class: 'volt-can', 'aria-hidden': 'true' },
        h('span', { class: 'volt-can__band' }),
        h('span', { class: 'volt-can__initial' }, (drink.brand || drink.title || '?').charAt(0).toUpperCase())
      );
    }

    renderUnavailable() {
      this.textContent = '';
      this.appendChild(
        h('div', { class: 'volt-flow volt-flow--empty' },
          h('p', { class: 'volt-flow__empty-note' }, 'The box builder is restocking — check back soon.')
        )
      );
    }

    /* ---------- skeleton (rendered once) ---------- */

    render() {
      this.textContent = '';
      var self = this;

      var back = h('button', {
        type: 'button', class: 'volt-flow__back', 'data-flow-back': true,
        'aria-label': 'Back',
        onclick: function () { self.back(); }
      }, '←');

      var steps = h('ol', { class: 'volt-flow__steps', 'data-flow-steps': true });

      var lucky = h('button', {
        type: 'button', class: 'volt-flow__lucky', 'data-flow-lucky': true,
        'aria-label': 'Surprise me — fill the rest of the box randomly',
        onclick: function () { self.surprise(); }
      }, 'Surprise');

      var stage = h('div', { class: 'volt-flow__stage', 'data-flow-stage': true });

      /* The tray is a compact, always-visible summary below the step nav.
         The forward CTA remains at the bottom so the summary never covers
         the flavor catalog. */
      var quantity = h('div', { class: 'volt-flow__quantity', 'data-flow-quantity': true },
        h('strong', { class: 'volt-flow__quantity-count', 'data-flow-count': true }, '0 of 0'),
        h('span', { class: 'volt-flow__quantity-label' }, 'cans picked')
      );
      var strip = h('button', {
        type: 'button', class: 'volt-flow__strip', 'data-flow-strip-btn': true,
        'aria-label': 'Open your box to review or remove cans',
        onclick: function () { self.toggleSheet(); }
      },
        h('span', { class: 'volt-flow__strip-cans', 'data-flow-strip': true }),
        h('span', { class: 'volt-flow__strip-caret', 'aria-hidden': 'true' }, '⌃')
      );
      var clear = h('button', {
        type: 'button', class: 'volt-flow__tray-clear', 'data-flow-clear': true,
        'aria-label': 'Clear your box',
        onclick: function () { self.clear(); }
      }, 'Clear box');
      var total = h('div', { class: 'volt-flow__total', 'data-flow-total-wrap': true },
        h('span', { class: 'volt-flow__total-label', 'data-flow-total-label': true }, 'Starting total'),
        h('strong', { class: 'volt-flow__total-value', 'data-flow-total': true }, '')
      );
      var cta = h('button', {
        type: 'button', class: 'volt-btn volt-flow__cta', 'data-flow-cta': true,
        onclick: function () { self.ctaAction(); }
      }, '');
      var tray = h('div', { class: 'volt-flow__tray', 'data-flow-tray': true },
        h('div', { class: 'volt-flow__tray-row' }, quantity, strip, total, clear)
      );
      var actions = h('div', { class: 'volt-flow__actions' }, cta);

      var scrim = h('div', {
        class: 'volt-flow__scrim', 'data-flow-scrim': true, hidden: true,
        onclick: function () { self.closeSheet(); }
      });
      var sheet = h('div', {
        class: 'volt-flow__sheet', 'data-flow-sheet': true, hidden: true,
        role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Your box'
      });

      this._onKeydown = function (evt) {
        if (evt.key === 'Escape' && self._sheetOpen) self.closeSheet();
      };
      this.addEventListener('keydown', this._onKeydown);

      this.appendChild(
        h('div', { class: 'volt-flow' },
          h('div', { class: 'volt-flow__top' }, back, steps, lucky),
          tray,
          stage,
          actions,
          scrim,
          sheet,
          h('p', { class: 'volt-flow__sr', role: 'status', 'aria-live': 'polite', 'data-flow-status': true })
        )
      );
    }

    /* ---------- stage renderers ---------- */

    renderStage(direction) {
      var stage = this.querySelector('[data-flow-stage]');
      if (!stage) return;
      stage.textContent = '';
      this._tiles = {};
      this._brandTabs = {};
      this._sizeCards = {};
      this._planCards = {};

      var panel;
      if (this._handoff) panel = this.handoffPanel();
      else if (this.step === 'size') panel = this.sizePanel();
      else if (this.step === 'flavors') panel = this.flavorPanel();
      else if (this.step === 'plan') panel = this.planPanel();
      else panel = this.reviewPanel();

      if (direction && !reducedMotion()) {
        panel.classList.add(direction === 'back' ? 'volt-flow__panel--in-back' : 'volt-flow__panel--in-fwd');
      }
      stage.appendChild(panel);
    }

    stageHead(title, sub) {
      return h('div', { class: 'volt-flow__stage-head' },
        h('h2', { class: 'volt-flow__stage-title' }, title),
        sub ? h('p', { class: 'volt-flow__stage-sub' }, sub) : null
      );
    }

    sizePanel() {
      var self = this;
      var grid = h('div', { class: 'volt-flow__sizes' });
      this.variants.forEach(function (variant) {
        var perCan = variant.minUnitCents ? 'from ' + moneyFromCents(variant.minUnitCents) + ' / can' : '';
        var card = h('button', {
          type: 'button',
          class: 'volt-flow__size',
          'aria-pressed': 'false',
          onclick: function () {
            self.setVariant(variant.id);
            card.classList.add('is-picked');
            /* Brief beat so the tap lands visually before the slide. */
            window.setTimeout(function () { self.goto('flavors'); }, 220);
          }
        },
          variant.cans === 12 ? h('span', { class: 'volt-flow__size-tag' }, 'Most popular') : null,
          h('span', { class: 'volt-flow__size-cans' }, String(variant.cans)),
          h('span', { class: 'volt-flow__size-label' }, 'cans'),
          h('span', { class: 'volt-flow__size-price' }, variant.price || ''),
          perCan ? h('span', { class: 'volt-flow__size-per' }, perCan) : null
        );
        self._sizeCards[variant.id] = card;
        grid.appendChild(card);
      });
      return h('div', { class: 'volt-flow__panel' },
        this.stageHead('How big is your box?', 'Choose the box size that fits your order.'),
        grid
      );
    }

    flavorPanel() {
      var self = this;

      var tabs = h('div', { class: 'volt-flow__brands', role: 'tablist', 'aria-label': 'Brands' });
      this.brands.forEach(function (brand) {
        var tab = h('button', {
          type: 'button',
          class: 'volt-flow__brand' + (brand === self.brand ? ' is-active' : ''),
          role: 'tab',
          'aria-selected': brand === self.brand ? 'true' : 'false',
          onclick: function () {
            self.brand = brand;
            Object.keys(self._brandTabs).forEach(function (key) {
              self._brandTabs[key].classList.toggle('is-active', key === brand);
              self._brandTabs[key].setAttribute('aria-selected', key === brand ? 'true' : 'false');
            });
            self.renderTiles();
            tab.scrollIntoView({ block: 'nearest', inline: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
            self.announce(brand + ' flavors.');
          }
        },
          h('span', {}, brand),
          h('span', { class: 'volt-flow__brand-count', 'data-brand-count': brand }, '')
        );
        self._brandTabs[brand] = tab;
        tabs.appendChild(tab);
      });

      var pager = h('div', { class: 'volt-flow__pager', 'data-flow-pager': true, role: 'tabpanel' });

      var panel = h('div', { class: 'volt-flow__panel volt-flow__panel--flavors' },
        tabs,
        pager,
        h('p', { class: 'volt-flow__hint' }, 'Tap a can to add it · tap the number to type an amount')
      );

      /* Tiles are injected after the panel exists so renderTiles can be
         reused when the brand tab changes. */
      this._pager = pager;
      this.renderTiles();
      return panel;
    }

    renderTiles() {
      var self = this;
      var pager = this._pager;
      if (!pager) return;
      pager.textContent = '';
      pager.scrollTop = 0;
      this._tiles = {};

      var list = this.allDrinks.filter(function (d) {
        return (d.brand || 'More') === self.brand;
      });

      list.forEach(function (drink) {
        var soldOut = drink.available === false;
        var unitPrice = self.unitPrice(drink);
        var addBtn = h('button', {
          type: 'button',
          class: 'volt-flow__tile-add',
          disabled: soldOut,
          'aria-label': soldOut ? drink.title + ' — sold out' : 'Add ' + drink.title + (unitPrice ? ' — ' + unitPrice : ''),
          onclick: soldOut ? null : function () { self.addDrink(drink.id, addBtn); }
        },
          self.drinkVisual(drink),
          h('span', { class: 'volt-flow__tile-name' }, self.flavorLabel(drink)),
          unitPrice ? h('span', { class: 'volt-flow__tile-price' }, unitPrice) : null,
          soldOut ? h('span', { class: 'volt-flow__tile-soldout' }, 'Sold out') : null
        );

        /* Once a flavor is in the box the tile grows a − n + stepper.
           The middle number is a real input — type 10, get 10. */
        var qty = self.qtyInput(drink, 'volt-flow__tile-qty');
        var minus = h('button', {
          type: 'button', class: 'volt-flow__tile-step',
          'aria-label': 'Remove one ' + drink.title,
          onclick: function (evt) {
            evt.stopPropagation();
            self.removeOne(drink.id);
          }
        }, '−');
        var plus = h('button', {
          type: 'button', class: 'volt-flow__tile-step',
          'aria-label': 'Add one ' + drink.title,
          onclick: function (evt) {
            evt.stopPropagation();
            self.addDrink(drink.id, addBtn);
          }
        }, '+');
        var stepper = h('div', { class: 'volt-flow__tile-stepper', hidden: true }, minus, qty, plus);

        var tile = h('div', { class: 'volt-flow__tile' + (soldOut ? ' is-soldout' : '') }, addBtn, stepper);
        self._tiles[drink.id] = { tile: tile, stepper: stepper, qty: qty, plus: plus };
        pager.appendChild(tile);
      });

      this.updateTiles();
    }

    planPanel() {
      var self = this;
      var options = [{ id: null, name: 'One-time box', desc: 'Your total is the sum of the cans you picked.', price: this.isFull() ? this.boxTotal() : this.variant.price }]
        .concat((this.variant.plans || []).map(function (plan) {
          return { id: plan.id, name: plan.name, desc: 'Skip, pause or cancel anytime.', price: plan.price };
        }));

      var list = h('div', { class: 'volt-flow__plans' });
      options.forEach(function (option) {
        var card = h('button', {
          type: 'button',
          class: 'volt-flow__plan',
          'aria-pressed': 'false',
          onclick: function () { self.setPlan(option.id); }
        },
          h('span', { class: 'volt-flow__plan-name' }, option.name),
          h('span', { class: 'volt-flow__plan-desc' }, option.desc),
          h('span', { class: 'volt-flow__plan-price' }, option.price || '')
        );
        self._planCards[option.id === null ? 'onetime' : option.id] = card;
        list.appendChild(card);
      });

      var hasPlans = (this.variant.plans || []).length > 0;
      return h('div', { class: 'volt-flow__panel' },
        this.stageHead('Purchase type', hasPlans
          ? 'Subscribers can swap every flavor between deliveries.'
        : 'Every box is priced from the exact cans in your mix.'),
        list
      );
    }

    reviewPanel() {
      var self = this;
      var list = h('div', { class: 'volt-flow__review-list', 'data-flow-review': true });

      var editRows = h('div', { class: 'volt-flow__review-meta' },
        h('button', {
          type: 'button', class: 'volt-flow__review-edit',
          onclick: function () { self.goto('size', true); }
        },
          h('span', {}, this.size + '-can box'),
          h('span', { class: 'volt-flow__review-edit-hint' }, 'change')
        ),
        h('button', {
          type: 'button', class: 'volt-flow__review-edit',
          onclick: function () { self.goto('plan', true); }
        },
          h('span', { 'data-flow-review-plan': true }, ''),
          h('span', { class: 'volt-flow__review-edit-hint' }, 'change')
        )
      );

      var reviewNote = (this.variant.plans || []).length
        ? 'You can change your mix any time before your next ship date.'
        : 'One-time purchase · ' + this.size + ' cans · ' + this.boxTotal() + ' total.';

      return h('div', { class: 'volt-flow__panel volt-flow__panel--review' },
        this.stageHead('Your box', 'One last look — tweak anything before it ships.'),
        editRows,
        list,
        h('p', { class: 'volt-flow__review-note' }, reviewNote)
      );
    }

    handoffPanel() {
      return h('div', { class: 'volt-flow__panel' },
        this.stageHead('Preview mode', 'On the live store this payload is POSTed to ' + this.addUrl +
          ' and the customer continues to ' + (this.afterAdd === 'checkout' ? 'checkout' : 'the cart') + '.'),
        h('pre', { class: 'volt-flow__handoff-json' }, JSON.stringify(this._handoff, null, 2))
      );
    }

    /* ---------- tray + sheet ---------- */

    ctaAction() {
      if (this.step === 'size') {
        this.goto('flavors');
      } else if (this.step === 'flavors') {
        if (this.isFull()) this.goto('plan');
        else this.announce(this.remaining() + ' more ' + (this.remaining() === 1 ? 'can' : 'cans') + ' to go.');
      } else if (this.step === 'plan') {
        this.goto('review');
      } else {
        this.addToCart();
      }
    }

    toggleSheet() {
      if (this._sheetOpen) this.closeSheet();
      else this.openSheet();
    }

    openSheet() {
      this._sheetOpen = true;
      var sheet = this.querySelector('[data-flow-sheet]');
      var scrim = this.querySelector('[data-flow-scrim]');
      sheet.hidden = false;
      scrim.hidden = false;
      this.renderSheet();
      var close = sheet.querySelector('[data-flow-sheet-close]');
      if (close) close.focus();
    }

    closeSheet() {
      this._sheetOpen = false;
      this.querySelector('[data-flow-sheet]').hidden = true;
      this.querySelector('[data-flow-scrim]').hidden = true;
      var strip = this.querySelector('[data-flow-strip-btn]');
      if (strip) strip.focus();
    }

    renderSheet() {
      var self = this;
      var sheet = this.querySelector('[data-flow-sheet]');
      if (!sheet || !this._sheetOpen) return;
      var scroll = sheet.querySelector('[data-flow-sheet-list]');
      var scrollTop = scroll ? scroll.scrollTop : 0;
      sheet.textContent = '';

      var groups = this.groupedMix();
      var list = h('div', { class: 'volt-flow__sheet-list', 'data-flow-sheet-list': true });
      if (!groups.length) {
        list.appendChild(h('p', { class: 'volt-flow__sheet-empty' }, 'Nothing in your box yet — tap flavors to fill it.'));
      }
      groups.forEach(function (group) {
        var qty = self.qtyInput(group.drink, 'volt-flow__row-count volt-flow__row-qty');
        qty.value = String(group.count);
        list.appendChild(
          h('div', { class: 'volt-flow__row' },
            self.drinkVisual(group.drink),
            h('div', { class: 'volt-flow__row-text' },
              h('span', { class: 'volt-flow__row-brand' }, group.drink.brand || ''),
              h('span', { class: 'volt-flow__row-name' }, self.flavorLabel(group.drink)),
              h('span', { class: 'volt-flow__row-price' }, self.unitPrice(group.drink) + ' · ' + moneyFromCents(self.unitPriceCents(group.drink) * group.count))
            ),
            h('div', { class: 'volt-flow__row-stepper' },
              h('button', {
                type: 'button', class: 'volt-flow__step-btn',
                'aria-label': 'Remove one ' + group.drink.title,
                onclick: function () { self.removeOne(group.id); }
              }, '−'),
              qty,
              h('button', {
                type: 'button', class: 'volt-flow__step-btn',
                'aria-label': 'Add one ' + group.drink.title,
                disabled: self.isFull(),
                onclick: function () { self.addDrink(group.id); }
              }, '+')
            )
          )
        );
      });

      sheet.appendChild(
        h('div', { class: 'volt-flow__sheet-head' },
          h('p', { class: 'volt-flow__sheet-title' }, 'Your box — ' + this.state.mix.length + '/' + this.size),
          h('button', {
            type: 'button', class: 'volt-flow__sheet-close', 'data-flow-sheet-close': true,
            'aria-label': 'Close',
            onclick: function () { self.closeSheet(); }
          }, '✕')
        )
      );
      sheet.appendChild(list);
      if (groups.length) {
        sheet.appendChild(
          h('div', { class: 'volt-flow__sheet-foot' },
            h('button', {
              type: 'button', class: 'volt-flow__sheet-clear',
              onclick: function () { self.clear(); }
            }, 'Clear the box'),
            !this.isFull() ? h('button', {
              type: 'button', class: 'volt-flow__sheet-fill',
              onclick: function () { self.surprise(); }
            }, 'Fill the rest for me') : null
          )
        );
      }
      if (scroll) {
        var newScroll = sheet.querySelector('[data-flow-sheet-list]');
        if (newScroll) newScroll.scrollTop = scrollTop;
      }
    }

    /* Can flies from the tapped tile into the tray quantity block. Pure decoration —
       skipped for reduced motion. */
    flyToTray(fromEl) {
      if (reducedMotion() || !fromEl.animate) return;
      var can = fromEl.querySelector('.volt-can');
      var quantity = this.querySelector('[data-flow-quantity]');
      if (!can || !quantity) return;
      var from = can.getBoundingClientRect();
      var to = quantity.getBoundingClientRect();
      var ghost = can.cloneNode(true);
      ghost.className += ' volt-flow__ghost';
      ghost.style.left = from.left + 'px';
      ghost.style.top = from.top + 'px';
      ghost.style.width = from.width + 'px';
      ghost.style.height = from.height + 'px';
      document.body.appendChild(ghost);
      var dx = to.left + to.width / 2 - (from.left + from.width / 2);
      var dy = to.top + to.height / 2 - (from.top + from.height / 2);
      ghost.animate(
        [
          { transform: 'translate(0, 0) scale(1)', opacity: 1 },
          { transform: 'translate(' + dx * 0.5 + 'px, ' + (dy - 40) + 'px) scale(0.7)', opacity: 0.9, offset: 0.6 },
          { transform: 'translate(' + dx + 'px, ' + dy + 'px) scale(0.15)', opacity: 0.4 }
        ],
        { duration: 480, easing: 'cubic-bezier(0.3, 0.7, 0.4, 1)' }
      ).onfinish = function () {
        ghost.remove();
      };
      this.pulseTray(500);
    }

    pulseTray(delay) {
      if (reducedMotion()) return;
      var quantity = this.querySelector('[data-flow-quantity]');
      if (!quantity) return;
      window.setTimeout(function () {
        quantity.classList.remove('is-pulsing');
        void quantity.offsetWidth; /* restart the animation */
        quantity.classList.add('is-pulsing');
      }, delay || 0);
    }

    nudgeTray() {
      if (reducedMotion()) return;
      var tray = this.querySelector('[data-flow-tray]');
      if (!tray) return;
      tray.classList.remove('is-nudging');
      void tray.offsetWidth;
      tray.classList.add('is-nudging');
    }

    /* ---------- incremental refresh ---------- */

    update() {
      var self = this;
      var filled = this.state.mix.length;
      var size = this.size;
      var full = this.isFull();

      // Header: back button + step segments
      var backBtn = this.querySelector('[data-flow-back]');
      if (backBtn) backBtn.style.visibility = (this.step === 'size' && !this.exitUrl) ? 'hidden' : 'visible';

      var steps = this.querySelector('[data-flow-steps]');
      if (steps) {
        steps.textContent = '';
        var currentIndex = STEPS.indexOf(this.step);
        STEPS.forEach(function (step, index) {
          var allowed = self.stepAllowed(step);
          steps.appendChild(
            h('li', { class: 'volt-flow__step-item' + (index === currentIndex ? ' is-current' : '') + (index < currentIndex ? ' is-done' : '') },
              h('button', {
                type: 'button',
                class: 'volt-flow__step-btn-nav',
                disabled: !allowed,
                'aria-current': index === currentIndex ? 'step' : null,
                onclick: function () { self.goto(step, index < currentIndex); }
              },
                h('span', { class: 'volt-flow__step-dot', 'aria-hidden': 'true' }),
                h('span', { class: 'volt-flow__step-label' }, STEP_LABELS[step])
              )
            )
          );
        });
      }

      // Surprise-me shortcut only earns header space while picking flavors
      var lucky = this.querySelector('[data-flow-lucky]');
      if (lucky) lucky.hidden = !(this.step === 'flavors' && !full);

      // Quantity and live total stay separate so neither value has to wrap.
      var count = this.querySelector('[data-flow-count]');
      if (count) {
        count.textContent = filled + ' of ' + size;
        count.setAttribute('aria-label', filled + ' of ' + size + ' cans picked');
      }
      var totalLabel = this.querySelector('[data-flow-total-label]');
      var total = this.querySelector('[data-flow-total]');
      if (totalLabel) totalLabel.textContent = filled ? 'Current total' : 'Starting total';
      if (total) total.textContent = filled ? this.boxTotal() : (this.variant ? this.variant.price.replace(/^From\s+/i, '') : '');

      var clear = this.querySelector('[data-flow-clear]');
      if (clear) clear.disabled = !filled || this._submitting;

      // Can strip: newest addition always lands in view
      var strip = this.querySelector('[data-flow-strip]');
      if (strip) {
        strip.textContent = '';
        if (!filled) {
          strip.appendChild(h('span', { class: 'volt-flow__strip-hint' },
            this.step === 'size' ? 'Your box builds here' : 'Tap flavors to fill your box'));
        } else {
          this.state.mix.forEach(function (id) {
            var drink = self.drinkById(id);
            if (drink) strip.appendChild(h('span', { class: 'volt-flow__strip-can' }, self.drinkVisual(drink)));
          });
        }
        strip.parentElement.classList.toggle('is-empty', !filled);
        strip.scrollLeft = strip.scrollWidth;
      }

      // The one CTA
      var cta = this.querySelector('[data-flow-cta]');
      if (cta) {
        var label, waiting = false;
        var price = full ? this.boxTotal() : (this.variant ? this.variant.price : '');
        if (this._handoff) {
          label = 'Preview complete';
          waiting = true;
        } else if (this.step === 'size') {
          label = 'Next: pick flavors →';
        } else if (this.step === 'flavors') {
          if (full) label = 'Next: delivery →';
          else {
            label = this.remaining() + ' more ' + (this.remaining() === 1 ? 'can' : 'cans') + ' to go';
            waiting = true;
          }
        } else if (this.step === 'plan') {
          label = 'Review your box →';
        } else {
          label = 'Add to cart' + (price ? ' — ' + price : '');
        }
        cta.textContent = '';
        cta.appendChild(h('span', {}, label));
        cta.classList.toggle('volt-flow__cta--waiting', waiting);
        cta.disabled = this._submitting;
        if (this._submitting) cta.setAttribute('aria-busy', 'true');
        else cta.removeAttribute('aria-busy');
      }

      // Stage bits that update in place
      this.updateTiles();

      Object.keys(this._brandTabs || {}).forEach(function (brand) {
        var badge = self._brandTabs[brand].querySelector('[data-brand-count]');
        var n = self.brandCount(brand);
        badge.textContent = n ? String(n) : '';
        badge.classList.toggle('is-on', n > 0);
      });

      Object.keys(this._sizeCards || {}).forEach(function (id) {
        var active = Number(id) === self.state.variantId;
        self._sizeCards[id].classList.toggle('is-active', active);
        self._sizeCards[id].setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      Object.keys(this._planCards || {}).forEach(function (key) {
        var active = key === 'onetime' ? self.state.planId === null : Number(key) === self.state.planId;
        self._planCards[key].classList.toggle('is-active', active);
        self._planCards[key].setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      if (this.step === 'review' && !this._handoff) this.updateReview();
      if (this._sheetOpen) this.renderSheet();
    }

    updateTiles() {
      var self = this;
      var full = this.isFull();
      Object.keys(this._tiles || {}).forEach(function (id) {
        var refs = self._tiles[id];
        var n = self.countOf(Number(id));
        refs.stepper.hidden = !n;
        /* Never clobber a value mid-typing */
        if (document.activeElement !== refs.qty) refs.qty.value = n ? String(n) : '';
        refs.plus.disabled = full;
        refs.tile.classList.toggle('is-in', n > 0);
        refs.tile.classList.toggle('is-capped', full && !n);
      });
    }

    updateReview() {
      var self = this;
      var list = this.querySelector('[data-flow-review]');
      if (!list) return;
      var scrollTop = list.scrollTop;
      list.textContent = '';
      this.groupedMix().forEach(function (group) {
        var qty = self.qtyInput(group.drink, 'volt-flow__row-count volt-flow__row-qty');
        qty.value = String(group.count);
        list.appendChild(
          h('div', { class: 'volt-flow__row' },
            self.drinkVisual(group.drink),
            h('div', { class: 'volt-flow__row-text' },
              h('span', { class: 'volt-flow__row-brand' }, group.drink.brand || ''),
              h('span', { class: 'volt-flow__row-name' }, self.flavorLabel(group.drink)),
              h('span', { class: 'volt-flow__row-price' }, self.unitPrice(group.drink) + ' · ' + moneyFromCents(self.unitPriceCents(group.drink) * group.count))
            ),
            h('div', { class: 'volt-flow__row-stepper' },
              h('button', {
                type: 'button', class: 'volt-flow__step-btn',
                'aria-label': 'Remove one ' + group.drink.title,
                onclick: function () { self.removeOne(group.id); }
              }, '−'),
              qty,
              h('button', {
                type: 'button', class: 'volt-flow__step-btn',
                'aria-label': 'Add one ' + group.drink.title,
                disabled: self.isFull(),
                onclick: function () { self.addDrink(group.id); }
              }, '+')
            )
          )
        );
      });
      list.scrollTop = scrollTop;

      var planLabel = this.querySelector('[data-flow-review-plan]');
      if (planLabel) planLabel.textContent = this.plan ? this.plan.name : 'One-time purchase · ' + this.boxTotal();

      /* Editing on the review screen can empty slots — bounce back to
         flavors so the flow never strands on a step it wouldn't allow. */
      if (!this.stepAllowed('review')) this.goto('flavors', true);
    }
  }

  customElements.define('volt-box-flow', VoltBoxFlow);
})();
